import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import {
  asyncScheduler,
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  from,
  Observable,
  of,
  Subject,
  throwError,
} from 'rxjs';
import { catchError, filter, first, map, switchMap, throttleTime } from 'rxjs/operators';
import type { CognitoUserSession } from 'amazon-cognito-identity-js';
import { CognitoService } from './cognito/cognito.service';
import { PlatformService } from './cognito/platform.service';
import { SafariViewWrapperService } from './cognito/safari-view-wrapper.service';
import { webCognitoCallback } from './cognito/web-callback';
import {
  BASE_API_URL,
  COGNITO_CALLBACK,
  COGNITO_CALLBACK_APP,
  COGNITO_CALLBACK_IOS,
  COGNITO_CLIENT_ID,
  COGNITO_DOMAIN,
  HelperFunctions,
  IAddress,
  IAffiliateType,
  IUser,
  Role,
  User,
} from '@curbnturf/entities';
import { UserApiService } from '../backend-api/user-api.service';
import { createIdbKvStore } from '../storage/idb-kv-store.util';
import {
  APP_PREF_PREFIX,
  appPreferencesStorage,
  cognitoPreferencesStorage,
} from '../storage/cognito-preferences-storage.util';
import { ToastService } from '../toast/toast.service';
import { NetworkStatusService } from '../network/network-status.service';
import { AnalyticsService } from '../analytics/analytics.service';

// This service talks to a local fork of @curbnturf/auth's CognitoService
// (../cognito/cognito.service — see that file's header) and a local fork of
// @curbnturf/user's UserService (../backend-api/user-api.service). Session
// state lives entirely in local BehaviorSubjects (_authSession$, _fullUser$),
// not NgRx. CognitoService.authenticateUser() already resolves
// NEW_PASSWORD_REQUIRED as a plain value (`{ ...attrs, temporaryPassword:
// true }`), not a side-channel Actions-stream event, so no race is needed —
// this was the original reason (see git history, commit 2290b52) this
// service stopped routing through @curbnturf/auth's AuthFacade +
// LegacyAuthService + auth.effects.ts, which raced a Cognito call against
// the shared @ngrx/effects Actions stream to detect that same case.
//
// signUp()/completeFederatedSignIn()/updateProfile() used to race the `user`
// NgRx slice's Actions stream the same way (UserActions.userCreated/
// userUpdated/loginUser) after dispatching into UserFacade — replaced below
// with direct UserService.create()/.update() calls, branching on the
// Observable's own success/error instead of a second independent stream
// listener. Same category of fix as the Cognito case above, not just an
// NgRx removal.

const AUTH_API_URL = BASE_API_URL + 'auth';
const USER_API_URL = BASE_API_URL + 'user';

export type IdType = 'drivers-license' | 'passport' | 'state-id';

export interface INotifPrefs {
  emailUpdates: boolean;
  marketing: boolean;
  hostResponses: boolean;
  tripReminders: boolean;
}

export const DEFAULT_NOTIF_PREFS: INotifPrefs = {
  emailUpdates: true,
  marketing: false,
  hostResponses: true,
  tripReminders: true,
};

export interface IPublicUser {
  /** Real backend numeric user id (IUser.id) — undefined until a real
   *  backend user record exists for this session (see signUp() note). */
  id?: number;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  createdAt: string;
  verified?: boolean;
  verifiedAt?: string;
  idType?: IdType;
  idLastFour?: string;
  /** Real backend `IUser.photo` (`ISubPhoto`), rendered as a display URL via
   *  `HelperFunctions.getPhotoUrlSquareSmall()` — see `assembleFromIUser()`. */
  photoUrl?: string;
  notifPrefs?: INotifPrefs;
  /** Real backend `IUser.affiliateRef` — a unique code, generated via `GET
   *  affiliate/code-valid/:refCode`, used to attribute other users who sign up through
   *  this user's `/refer` link (matched server-side on `source`, see `ContactService`).
   *  Not tied to `affiliateType`/the RAF-Grow earnings program — just plain attribution. */
  affiliateRef?: string;
  /** Real backend `IUser.role` — drives sales/admin-only UI (e.g. the Grow clients tool),
   *  and, once set, which post-sign-in onboarding wizard (if any) applies. */
  role?: Role;
  /** Real backend `IUser.source` — "how did you hear about us" attribution. Set either
   *  from a `?ref=` referral link at signup (see `readReferralSource()`) or by the
   *  `/welcome/source` onboarding step; once set it's never asked again. */
  source?: string;
  /** Real backend `IUser.instantBookingAllowed` — admin-granted permission. Only
   *  hosts with it can see/choose Instant Book in the listing wizard; everyone
   *  else's listings are request-to-book (the server enforces this too). */
  instantBookingAllowed?: boolean;
  /** Real backend `IUser.affiliateType` — set once the user joins the Grow ('grow')
   *  or Refer-a-Friend ('raf') earnings program via `/grow/apply`. */
  affiliateType?: IAffiliateType['type'];
}

export type ProfilePatch = Partial<
  Pick<
    IPublicUser,
    'firstName' | 'lastName' | 'phone' | 'notifPrefs' | 'affiliateRef' | 'role' | 'source' | 'affiliateType'
  > & {
    /** Backend-only — `IPublicUser` doesn't surface the address. */
    address: IAddress;
    sendSmss: boolean;
  }
>;

export type AppView = 'guest' | 'host';
export type FederatedProvider = 'Google' | 'Apple' | 'Facebook';

/** Legacy raw localStorage key — see `migrateLegacyViewKey()`. */
const LEGACY_VIEW_KEY = 'cnt-view-mode';
/** appPreferencesStorage key. Must start with APP_PREF_PREFIX — that's what
 *  its hydrate()'s Preferences-keys() scan filters on, so an unprefixed key
 *  written via setItem() would round-trip within one page load (read from
 *  the in-memory cache) but never be found again by hydrate() on a fresh
 *  boot, even though it's really sitting in Preferences. */
const VIEW_KEY = `${APP_PREF_PREFIX}view-mode`;
/** Stores ID-verification + UI-only prefs that neither Cognito nor IUser own (notifPrefs, idType, idLastFour). */
const LOCAL_PROFILE_KEY = 'cnt-local-profile';
/** Legacy localStorage key for referral attribution — see `setReferralSource`. */
const REFERRED_BY_KEY = 'cnt-referred-by';

const LOCAL_PROFILE_IDB_KEY = 'auth:local-profile';
const REFERRED_BY_IDB_KEY = 'auth:referred-by';

interface ILocalProfile {
  [email: string]: {
    verified?: boolean;
    verifiedAt?: string;
    idType?: IdType;
    idLastFour?: string;
    notifPrefs?: INotifPrefs;
  };
}

export type SignInResult =
  | { ok: true; user: IPublicUser }
  | { ok: false; needsNewPassword?: false; error: string }
  | { ok: false; needsNewPassword: true; email: string };
export type SignUpResult = { ok: true; user: IPublicUser; needsConfirmation: boolean } | { ok: false; error: string };
export type CodeResult = { ok: true } | { ok: false; error: string };
export type ForgotPasswordResult = { ok: true; temporaryPasswordSent: boolean } | { ok: false; error: string };

/** The server stores `user.created` in epoch SECONDS (UserService.create); read
 *  as milliseconds it lands in Jan 1970 (F37). A value already in milliseconds
 *  (above ~2001 in ms, i.e. > 1e12) is passed through so a caller handing over
 *  a JS timestamp still works. */
export function createdToIso(created: number | undefined | null): string {
  if (!created) return new Date().toISOString();
  return new Date(created > 1e12 ? created : created * 1000).toISOString();
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private http = inject(HttpClient);
  private cognitoService = inject(CognitoService);
  private analytics = inject(AnalyticsService);
  private userSvc = inject(UserApiService);
  private toasts = inject(ToastService);
  private networkStatus = inject(NetworkStatusService);
  private platformService = inject(PlatformService);
  private safariView = inject(SafariViewWrapperService);

  /** Local session source of truth — replaces the old AuthFacade.user$ (the
   *  `auth` NgRx slice's `state.user`). Same whitelisted-fields shape: tokens
   *  plus id/email/firstName/lastName/role. */
  private readonly _authSession$ = new BehaviorSubject<IUser | undefined>(undefined);

  /** Full `/user/me` backend record — replaces the old `userFacade.currentUser$`
   *  (the `user` NgRx slice) as the constructor's combineLatest source below,
   *  and what payouts.component.ts reads for its full User-with-transactions
   *  view. Populated in applySession(). */
  private readonly _fullUser$ = new BehaviorSubject<IUser | null>(null);
  readonly fullUser$: Observable<IUser | null> = this._fullUser$.asObservable();

  private readonly _currentUser$ = new BehaviorSubject<IPublicUser | null>(null);
  readonly currentUser$: Observable<IPublicUser | null> = this._currentUser$.asObservable();

  private readonly _currentView$ = new BehaviorSubject<AppView>('guest');
  readonly currentView$: Observable<AppView> = this._currentView$.asObservable();

  private readonly localProfileStore = createIdbKvStore<ILocalProfile>({
    legacyKeys: { [LOCAL_PROFILE_IDB_KEY]: LOCAL_PROFILE_KEY },
  });
  private readonly referredByStore = createIdbKvStore<string>({
    legacyKeys: { [REFERRED_BY_IDB_KEY]: REFERRED_BY_KEY },
  });

  private readonly renewTrigger$ = new Subject<void>();
  private readonly _renewResult$ = new Subject<'success' | 'deferred'>();

  /** Counts in-flight `applySession()` calls. `_authSession$` optimistically
   *  goes truthy (id-less) before the real backend id is merged in below —
   *  `currentUserOnceLoaded()` waits for this to hit 0 so callers never read
   *  that transient id-less snapshot. A counter (not a single promise field)
   *  so a nested call — e.g. a token-renewal `applySession()` firing via
   *  `renew()` while an outer one is still pending — can't have its `finally`
   *  clear a flag the other call still owns. */
  private readonly _sessionSettling$ = new BehaviorSubject<number>(0);

  /** Fires once per `signOut()` call, after the outgoing user's session is
   *  known but before it's cleared — every store holding personal data for
   *  that user (payments, bookings, drafts, quick replies, etc.) subscribes
   *  to this and clears itself. Not derived from `currentUser$`/`_authSession$`
   *  going to `null`, since that's also true on a fresh, never-signed-in page
   *  load — this needs to be an explicit "a sign-out just happened" event. */
  private readonly _loggedOut$ = new Subject<void>();
  readonly loggedOut$: Observable<void> = this._loggedOut$.asObservable();

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      void this.localProfileStore.init();
      void this.referredByStore.init();
    }

    // Mirror the local session + full backend profile into the existing
    // BehaviorSubject so `currentUser`/`currentUser$` keep their same
    // synchronous-getter contract for existing consumers.
    //
    // The session (authSession$) is not full-fidelity: applySession() below
    // only ever whitelists id/email/firstName/lastName/role onto it, dropping
    // source/phone/photo/etc. even though it fetches the real full user from
    // GET /user/me in the same call — that full response goes into
    // `_fullUser$` instead. Combine both: authSession gates "is anyone
    // logged in", fullUser supplies the complete field set once it's caught
    // up (matched by id — there's a brief window right after login where the
    // session has tokens but the /user/me fetch hasn't resolved yet).
    combineLatest([this._authSession$, this._fullUser$]).subscribe(([authUser, fullUser]) => {
      if (!authUser) {
        this._currentUser$.next(null);
        return;
      }
      const source = fullUser?.id && fullUser.id === authUser.id ? fullUser : authUser;
      this._currentUser$.next(this.assembleFromIUser(source));
    });

    if (isPlatformBrowser(this.platformId)) {
      this.hydrateView();
      // Restore a previously-signed-in session on app boot. CognitoUserPool
      // (amazon-cognito-identity-js, inside CognitoService) already persists
      // its own session in localStorage independent of anything NgRx-related
      // — this reads that directly instead of relying on NgRx-persisted
      // `auth` state. Fire-and-forget here (must never block bootstrap);
      // currentUserOnceLoaded() below is what callers actually await.
      void this.restoreSessionOnce();
    }

    // Silent token renewal, triggered by the HTTP interceptor (triggerRenew())
    // when a request is about to be made with an expired/near-expiry token.
    // Throttled + coalesced so several concurrent requests share one renewal.
    this.renewTrigger$.pipe(throttleTime(3000, asyncScheduler, { leading: true, trailing: false })).subscribe(() => {
      this.renew()
        .pipe(
          catchError((err: unknown) => {
            // err.name !== 'HttpErrorResponse' means we just aren't connected
            // to the internet, so we don't want to sign out until they've had
            // a chance to retry connecting.
            const httpErr = err as { name?: string; error?: { message?: string } };
            if (
              httpErr?.name !== 'HttpErrorResponse' ||
              httpErr.error?.message === 'Invalid Refresh Token' ||
              httpErr.error?.message === 'Refresh Token has expired'
            ) {
              void this.signOut('Your session has timed out.');
            }
            return throwError(() => err);
          }),
        )
        .subscribe({
          next: (result) => this._renewResult$.next(result),
          error: () => {
            /* signOut already triggered above where applicable */
          },
        });
    });
  }

  // ---- View toggle (unchanged — purely UI state) ----------------------

  get currentView(): AppView {
    return this._currentView$.value;
  }
  get currentUser(): IPublicUser | null {
    return this._currentUser$.value;
  }

  /**
   * Like `currentUser`, but waits for a previously-signed-in session to
   * actually finish being restored (or confirmed absent) first, instead of
   * racing app bootstrap. `currentUser`'s synchronous getter can read `null`
   * for an actually-signed-in user on a fresh page load/refresh, before
   * restoration has run — anything that makes a logged-in/out decision at
   * page-load time (guards, `ngOnInit` checks) must use this instead, or
   * it'll intermittently treat a signed-in user as logged out.
   */
  async currentUserOnceLoaded(): Promise<IPublicUser | null> {
    if (isPlatformBrowser(this.platformId)) {
      await this.restoreSessionOnce();
      await firstValueFrom(this._sessionSettling$.pipe(filter((n) => n === 0)));
    }
    return this._currentUser$.value;
  }

  setView(v: AppView): void {
    this._currentView$.next(v);
    if (isPlatformBrowser(this.platformId)) {
      appPreferencesStorage.setItem(VIEW_KEY, v);
    }
  }

  /**
   * Reads whatever's already in `appPreferencesStorage`'s in-memory cache.
   * Called synchronously here (constructor) — in the common case the boot
   * APP_INITIALIZER's `hydrate()` has already resolved by the time
   * AuthService is first constructed, but this isn't a hard guarantee (see
   * restoreSession()'s own `await hydrate()` for the case that *is*
   * guaranteed — session tokens). A miss here just means the view briefly
   * shows the 'guest' default, a cosmetic risk accepted for this small,
   * non-sensitive toggle rather than adding new re-hydration plumbing.
   */
  private hydrateView(): void {
    this.migrateLegacyViewKey();
    const v = appPreferencesStorage.getItem(VIEW_KEY);
    if (v === 'host' || v === 'guest') this._currentView$.next(v);
  }

  /** One-time migration off the raw `localStorage` key this app used before
   *  VIEW_KEY moved onto `appPreferencesStorage`. Unlike Cognito's own
   *  per-username keys, this key's name is fixed and known up front, so —
   *  unlike cognitoPreferencesStorage's hydrate()-time scan — this can run
   *  synchronously, without waiting on Preferences. */
  private migrateLegacyViewKey(): void {
    if (appPreferencesStorage.getItem(VIEW_KEY) != null) return;
    let legacy: string | null = null;
    try {
      legacy = localStorage.getItem(LEGACY_VIEW_KEY);
    } catch {
      /* quota / disabled */
    }
    if (legacy === 'host' || legacy === 'guest') {
      appPreferencesStorage.setItem(VIEW_KEY, legacy);
      try {
        localStorage.removeItem(LEGACY_VIEW_KEY);
      } catch {
        /* quota / disabled */
      }
    }
  }

  // ---- Sign-up / confirm (real, Cognito + backend-IUser-backed) --------

  /**
   * A raw Cognito signUp (via amazon-cognito-identity-js, through
   * CognitoService) followed immediately by a real backend `POST /user`
   * (userSvc.create). The two calls are independent — the backend IUser
   * is created right away, not gated on the email-confirmation step below.
   *
   * Cognito's signUp never surfaces the pool's `userConfirmed` flag here, so
   * `needsConfirmation` is assumed `true` (the pool's default — an
   * auto-confirmed pool would be the rare exception).
   */
  async signUp(input: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
  }): Promise<SignUpResult> {
    const email = input.email.trim().toLowerCase();
    const firstName = input.firstName.trim();
    const lastName = input.lastName.trim();
    const phone = input.phone?.trim() || undefined;
    try {
      await this.cognitoService.signUp(email, input.password);

      const created = await firstValueFrom(
        this.userSvc.create({ email, firstName, lastName, phone, source: this.readReferralSource() } as IUser),
      );
      const user: IPublicUser = {
        id: created.id,
        email,
        firstName,
        lastName,
        phone,
        createdAt: createdToIso(created.created),
      };
      this.analytics.track('sign_up', { method: 'email', source: this.readReferralSource() ?? '' });
      return { ok: true, user, needsConfirmation: true };
    } catch (e) {
      return { ok: false, error: friendlyError(e) };
    }
  }

  async confirmSignUp(username: string, code: string): Promise<CodeResult> {
    try {
      const ok = await firstValueFrom(
        this.http.get<boolean>(
          `${AUTH_API_URL}?confirmationCode=${code.trim()}&username=${username.trim().toLowerCase()}`,
        ),
      );
      return ok ? { ok: true } : { ok: false, error: 'That code is incorrect or has expired.' };
    } catch (e) {
      return { ok: false, error: friendlyError(e) };
    }
  }

  async resendConfirmation(email: string): Promise<CodeResult> {
    try {
      await firstValueFrom(this.cognitoService.resendVerification(email.trim().toLowerCase()));
      return { ok: true };
    } catch (e) {
      return { ok: false, error: friendlyError(e) };
    }
  }

  /** Referral attribution captured from `?ref=` at /signup (see sign-up.component.ts) — passed
   *  through as IUser.source, same as curbnturf's own register.component.ts. */
  private readReferralSource(): string | undefined {
    if (!isPlatformBrowser(this.platformId)) return undefined;
    return this.referredByStore.read(REFERRED_BY_IDB_KEY) || undefined;
  }

  /** Public read of the stored referral attribution, for flows that create an
   *  account outside `signUp()` (e.g. the Grow apply form's `POST /contact`). */
  getReferralSource(): string | undefined {
    return this.readReferralSource();
  }

  /** Called by sign-up.component.ts to persist `?ref=` — routed through this
   *  service (rather than writing localStorage/IDB directly) so the read in
   *  `readReferralSource()` above always sees it through the same store
   *  instance's cache, not a second independent one. */
  setReferralSource(ref: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    void this.referredByStore.write(REFERRED_BY_IDB_KEY, ref);
  }

  /** Attributes an eventual signup to a marketing landing page (e.g. 'facebook-farm').
   *  Called on every visit to a persona/marketing landing page, so the most recently
   *  visited marketing page gets credit (last-touch) — but never overwrites an explicit
   *  ?ref= friend/affiliate code (setReferralSource above), which always takes priority
   *  over passive ad-page attribution. */
  setLandingSource(source: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const existing = this.readReferralSource();
    if (existing && !existing.startsWith('facebook-')) return;
    void this.referredByStore.write(REFERRED_BY_IDB_KEY, source);
  }

  /**
   * Redirects to Cognito's Hosted UI for the given provider — same URL shape
   * and per-platform callback as curbnturf's SocialAuthComponent.buildSocialLoginLink.
   * The callback must match the one CognitoService.codeToUser() exchanges the
   * code with, or Cognito rejects the exchange (invalid_grant): native iOS
   * returns via the `curbnturf://` scheme, native Android via the
   * cognitoCallbackApp App Link, the web via cognitoCallback. On iOS the
   * Hosted UI opens in an SFSafariViewController, which AppComponent's
   * appUrlOpen handler closes when the `curbnturf://` redirect lands.
   * `returnTo` round-trips through Cognito's `state` param so /auth/redirect's
   * OAuth branch can send the user back where they started, same as password sign-in.
   */
  async signInWithProvider(provider: FederatedProvider, returnTo?: string): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    const url = this.socialLoginUrl(provider, returnTo);
    if (this.isNativeIOS() && (await this.safariView.isAvailable())) {
      await this.safariView.launchBrowserUI(url);
      return;
    }
    window.location.href = url;
  }

  private isNativeIOS(): boolean {
    return this.platformService.isPhysicalDevice() && this.platformService.isIOS();
  }

  private socialLoginUrl(provider: FederatedProvider, returnTo?: string): string {
    const callback = this.platformService.isPhysicalDevice()
      ? this.platformService.isIOS()
        ? COGNITO_CALLBACK_IOS
        : COGNITO_CALLBACK_APP
      : webCognitoCallback(COGNITO_CALLBACK);
    const idp = provider === 'Apple' ? 'SignInWithApple' : provider;
    const state = returnTo ? `&state=${encodeURIComponent(returnTo)}` : '';
    return (
      `https://${COGNITO_DOMAIN}/oauth2/authorize?redirect_uri=${encodeURIComponent(callback)}` +
      `&response_type=code&client_id=${COGNITO_CLIENT_ID}&identity_provider=${idp}${state}`
    );
  }

  /**
   * Called by AuthRedirectComponent's OAuth branch once Cognito redirects
   * back with a `code`. Exchanges the code for tokens directly via
   * CognitoService, then
   * creates/dedupes a real backend IUser via `POST /user` and applies the
   * session directly on success — same merge (tokens + backend id) that
   * user.effects.ts's createUser$ effect used to produce via a
   * UserActions.loginUser dispatch, just without the extra Actions-stream
   * round trip. (No referral-source attribution here — `readReferralSource()`
   * only covers plain email signUp(); federated sign-in never wrote a
   * `source` value in this app, so nothing is dropped by not reading one.)
   *
   * Awaits the full applySession() merge (real backend id included) before
   * resolving — callers (AuthRedirectComponent) must not treat the session as
   * ready off the bare `currentUser` getter before this settles, or they can
   * observe applySession()'s transient id-less optimistic value.
   */
  async completeFederatedSignIn(code: string): Promise<void> {
    const user = await firstValueFrom(this.cognitoService.codeToUser(code.trim()));
    if (!user.email) return;
    const payload: IUser = {
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      authAccessToken: user.authAccessToken,
      authIdToken: user.authIdToken,
      authRenewToken: user.authRenewToken,
      authExpires: user.authExpires,
    };
    const newUser: IUser = {
      ...payload,
      authAccessToken: undefined,
      authIdToken: undefined,
      authRenewToken: undefined,
      authExpires: undefined,
    };
    const created = await firstValueFrom(this.userSvc.create(newUser));
    // Only the backend id — not the whole create() row. That row is a
    // login-time snapshot (phone/photo/etc.), and anything spread in here
    // rides along on `_authSession$` into later `userSvc.update()` PUTs,
    // where it overwrites profile fields saved since (e.g. the guest
    // wizard's phone + photo, wiped by its own "add your RV" step).
    await this.applySession({ ...payload, id: created.id });
  }

  // ---- Sign-in / sign-out (real, Cognito-backed via CognitoService) ----

  async signIn(email: string, password: string): Promise<SignInResult> {
    const normalizedEmail = email.trim().toLowerCase();
    try {
      const cognitoUser = await this.cognitoService.authenticateUser(normalizedEmail, password.trim());
      if (cognitoUser.temporaryPassword) {
        return { ok: false, needsNewPassword: true, email: normalizedEmail };
      }
      const assembled = await this.applySession(cognitoUser);
      return assembled
        ? { ok: true, user: assembled }
        : { ok: false, error: 'Sign-in succeeded but user load failed.' };
    } catch (e) {
      const code = (e as { code?: string })?.code;
      if (code === 'UserNotConfirmedException') {
        this.router.navigate(['/auth/confirm'], { queryParams: { email: normalizedEmail } });
        return { ok: false, error: 'Please verify your email before logging in.' };
      }
      return { ok: false, error: friendlyError(e) };
    }
  }

  async signOut(message = 'Signed out'): Promise<void> {
    const session = this._authSession$.value;
    if (session?.authIdToken && session?.authAccessToken && session?.authRenewToken) {
      try {
        await this.cognitoService.signOut({
          IdToken: session.authIdToken,
          AccessToken: session.authAccessToken,
          RefreshToken: session.authRenewToken,
        });
      } catch {
        // best-effort — still clear the local session below either way
      }
    }

    // Drop this user's own local-only profile fields (ID-verification status,
    // notifPrefs) before broadcasting loggedOut$ — other accounts that have
    // used this device keep their own entries in the same map.
    const outgoingEmail = session?.email;
    if (outgoingEmail) {
      const all = this.readLocalProfile();
      if (outgoingEmail in all) {
        delete all[outgoingEmail];
        void this.localProfileStore.write(LOCAL_PROFILE_IDB_KEY, all);
      }
    }
    this._loggedOut$.next();

    this._authSession$.next(undefined);
    this._fullUser$.next(null);
    if (message) {
      this.toasts.success(message);
    }

    this._currentView$.next('guest');
    if (isPlatformBrowser(this.platformId)) {
      appPreferencesStorage.removeItem(VIEW_KEY);
    }
    await this.router.navigateByUrl('/');
  }

  // ---- Forgot password (real, Cognito-backed) --------------------------

  /**
   * Backend POST /auth/forgot returns false (instead of true) when the
   * account already has a temporary password — in that case it emails a
   * brand-new temporary password instead of a verification code, and no
   * Cognito forgot-password challenge is started. Callers must route the
   * user back to sign-in (with the new temp password) rather than to the
   * code-entry reset-password page in that case.
   */
  async forgotPassword(email: string): Promise<ForgotPasswordResult> {
    try {
      const codeSent = await firstValueFrom(
        this.http.post<boolean>(`${AUTH_API_URL}/forgot`, { email: email.trim().toLowerCase() }),
      );
      return { ok: true, temporaryPasswordSent: !codeSent };
    } catch (e) {
      return { ok: false, error: friendlyError(e) };
    }
  }

  /** Completes a Cognito NEW_PASSWORD_REQUIRED challenge (see signIn()). */
  async completeNewPassword(newPassword: string): Promise<CodeResult> {
    try {
      await this.cognitoService.newPassword(newPassword.trim());
      return { ok: true };
    } catch (e) {
      return { ok: false, error: friendlyError(e) };
    }
  }

  async confirmForgotPassword(email: string, code: string, newPassword: string): Promise<CodeResult> {
    try {
      await firstValueFrom(
        this.cognitoService.confirmResetPassword(email.trim().toLowerCase(), code.trim(), newPassword),
      );
      return { ok: true };
    } catch (e) {
      return { ok: false, error: friendlyError(e) };
    }
  }

  // ---- Profile updates (real, backend-IUser-backed via UserService) ----

  /**
   * `authSession` is only ever set by applySession()/signOut() — nothing
   * re-syncs it after a profile save, so re-reading it would just return the
   * same pre-save snapshot. Use the updated `IUser` UserService.update()
   * itself returns instead, and push the assembled result into
   * `_currentUser$` directly so `currentUser`/`currentUser$` reflect the save
   * immediately — callers that branch on the just-saved field right away
   * (e.g. `PostAuthRedirectService` after setting role/source) depend on
   * this being current, not stale.
   *
   * Returns `null` if a backend patch was attempted and actually failed —
   * callers must check for this rather than assuming the save always
   * succeeds.
   */
  async updateProfile(patch: ProfilePatch): Promise<IPublicUser | null> {
    const authUser = this._authSession$.value;
    if (!authUser) return null;
    // `_authSession$` never carries fields like `photo` (see the constructor's
    // combineLatest comment above) — prefer the matching `_fullUser$` snapshot,
    // same selection the constructor uses for `_currentUser$`, so a save right
    // after a photo upload (or any other `_fullUser$`-only change) doesn't
    // clobber it with a photo-less login-time snapshot.
    const fullUser = this._fullUser$.value;
    const currentIUser = fullUser?.id && fullUser.id === authUser.id ? fullUser : authUser;

    const backendPatch: Partial<IUser> = {};
    if (patch.firstName !== undefined) backendPatch.firstName = patch.firstName.trim();
    if (patch.lastName !== undefined) backendPatch.lastName = patch.lastName.trim();
    if (patch.phone !== undefined) backendPatch.phone = patch.phone.trim() || undefined;
    if (patch.affiliateRef !== undefined) backendPatch.affiliateRef = patch.affiliateRef;
    if (patch.role !== undefined) backendPatch.role = patch.role;
    if (patch.source !== undefined) backendPatch.source = patch.source;
    if (patch.affiliateType !== undefined) backendPatch.affiliateType = patch.affiliateType;
    if (patch.address !== undefined) backendPatch.address = patch.address;
    if (patch.sendSmss !== undefined) backendPatch.sendSmss = patch.sendSmss;

    let updatedIUser = currentIUser;
    if (Object.keys(backendPatch).length > 0) {
      // Cognito session tokens live on this object purely for client-side
      // token-refresh — never let them ride along into a save payload.
      const safeCurrentUser: IUser = { ...currentIUser };
      delete safeCurrentUser.authAccessToken;
      delete safeCurrentUser.authIdToken;
      delete safeCurrentUser.authRenewToken;
      delete safeCurrentUser.authExpires;
      try {
        updatedIUser = await firstValueFrom(this.userSvc.update({ ...safeCurrentUser, ...backendPatch }));
      } catch {
        return null;
      }
    }

    // Local-side prefs (notifPrefs) — no backend equivalent, unchanged from before.
    const emailKey = currentIUser.email ?? '';
    if (patch.notifPrefs !== undefined) {
      this.writeLocalProfile(emailKey, { notifPrefs: patch.notifPrefs });
    }

    // Push through `_fullUser$` (the constructor's combineLatest source),
    // not `_currentUser$` directly — `_currentUser$` is a derived value that
    // gets recomputed from `_fullUser$`/`_authSession$` on their *next* emission
    // from ANY source (e.g. a silent token renew's own `/user/me` refetch, see
    // `applySession()`). A direct `_currentUser$.next()` here is immediately
    // clobbered back to the pre-save value the moment that happens, since
    // `_fullUser$` itself was never told about this save. combineLatest still
    // recomputes synchronously on this `.next()`, so `currentUser`/`currentUser$`
    // reflect the save immediately, same as before.
    const assembled = this.assembleFromIUser(updatedIUser);
    this._fullUser$.next(updatedIUser);
    return assembled;
  }

  /**
   * Uploads a real profile photo via the same backend-persisted `IUser.photo`
   * pipeline the main curbnturf app's sign-up flow and this app's RV-photo
   * uploads already use (`POST /user/:id/image` — see `my-rv.util.ts`'s
   * `uploadRvPhoto()` for the identical FormData shape). Unlike `updateProfile()`,
   * this takes effect immediately rather than waiting for a batched save, since
   * the upload endpoint persists on its own.
   *
   * `crop` must be integer PIXEL offsets into `file` (not normalized 0..1) —
   * the backend's shared crop renderer (`createCropEdit` in
   * helpers/src/helpers/photo.ts) expects pixels for every photo type. The
   * default `{0,0,0,0}` is the renderer's "no crop" identity, matching the
   * pre-crop-modal behavior for any caller that doesn't crop.
   */
  async uploadProfilePhoto(
    file: File,
    crop: { left: number; top: number; right: number; bottom: number } = { left: 0, top: 0, right: 0, bottom: 0 },
    rotation = 0,
  ): Promise<IPublicUser | null> {
    const currentIUser = this._authSession$.value;
    if (!currentIUser?.id) return null;

    const form = new FormData();
    form.append('files', file, file.name);
    form.append('caption', '');
    form.append('cropLeft', String(crop.left));
    form.append('cropTop', String(crop.top));
    form.append('cropRight', String(crop.right));
    form.append('cropBottom', String(crop.bottom));
    form.append('zoom', '1');
    form.append('rotation', String(rotation));

    let updatedIUser: IUser;
    try {
      updatedIUser = await firstValueFrom(this.userSvc.uploadImage(currentIUser.id, form));
    } catch {
      return null;
    }
    // See the comment in updateProfile() — push through `_fullUser$`, the
    // actual combineLatest source, so a subsequent silent token renew can't
    // clobber the new photo back to the pre-upload value.
    const assembled = this.assembleFromIUser(updatedIUser);
    this._fullUser$.next(updatedIUser);
    return assembled;
  }

  /** Removes the real backend profile photo — see `uploadProfilePhoto()`. */
  async removeProfilePhoto(): Promise<IPublicUser | null> {
    const currentIUser = this._authSession$.value;
    if (!currentIUser?.id) return null;

    let updatedIUser: IUser;
    try {
      updatedIUser = await firstValueFrom(this.userSvc.removeImage(currentIUser.id));
    } catch {
      return null;
    }
    const assembled = this.assembleFromIUser(updatedIUser);
    this._fullUser$.next(updatedIUser);
    return assembled;
  }

  async updatePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<{ ok: true } | { ok: false; error: string }> {
    if (!newPassword || newPassword.length < 8) {
      return { ok: false, error: 'New password must be at least 8 characters.' };
    }
    const accessToken = this._authSession$.value?.authAccessToken;
    if (!accessToken) {
      return { ok: false, error: 'You must be signed in to change your password.' };
    }
    try {
      const success = await firstValueFrom(
        this.cognitoService.changePassword(currentPassword.trim(), newPassword.trim(), accessToken),
      );
      if (success) {
        // Best-effort, fire-and-forget — clears the server-side flag that
        // forces a password change; not worth blocking/failing this method
        // over if the full user record hasn't loaded yet.
        const user = this._fullUser$.value;
        if (user) this.userSvc.update({ ...user, adminPassword: false }).subscribe();
        return { ok: true };
      }
      return { ok: false, error: 'Unable to update password.' };
    } catch (e) {
      return { ok: false, error: friendlyError(e) };
    }
  }

  /** ID verification stays mock — neither Cognito nor IUser do KYC. Persists per-email in localStorage. */
  markVerified(input: { idType: IdType; idLastFour?: string }): IPublicUser | null {
    const current = this._currentUser$.value;
    if (!current) return null;
    this.writeLocalProfile(current.email, {
      verified: true,
      verifiedAt: new Date().toISOString(),
      idType: input.idType,
      idLastFour: input.idLastFour,
    });
    const updated: IPublicUser = {
      ...current,
      verified: true,
      verifiedAt: new Date().toISOString(),
      idType: input.idType,
      idLastFour: input.idLastFour,
    };
    this._currentUser$.next(updated);
    return updated;
  }

  // ---- Internal: session application + token renewal --------------------

  /**
   * Applies a Cognito-authenticated `IUser` (tokens + basic profile) as the
   * active session, then fetches `/user/me` to merge in the real backend
   * id/role. Shared by signIn(), completeFederatedSignIn(), and renew().
   * Returns the assembled public profile, or `null` if the session tokens
   * were invalid or `/user/me` failed (the session itself is still applied
   * either way so the caller stays signed in with a retryable state).
   */
  private async applySession(user: IUser): Promise<IPublicUser | null> {
    if (!user?.authIdToken || !user?.authRenewToken) return null;

    // Counter, not a plain flag/promise field — a nested applySession() call
    // (e.g. renew() firing mid-flight through this same method) must not have
    // its `finally` clear settling state that an outer, still-pending call
    // still owns.
    this._sessionSettling$.next(this._sessionSettling$.value + 1);
    try {
      this._authSession$.next(new User(user));

      try {
        const existingUser = await firstValueFrom(this.http.get<IUser>(`${USER_API_URL}/me`));
        if (!existingUser?.id) {
          // No backend user for this session (e.g. valid Cognito tokens for an
          // account the local/test DB doesn't know about) — undo the optimistic
          // _authSession$.next() above rather than leaving it on a role-less,
          // id-less stub forever. Left in place, currentUserOnceLoaded() would
          // permanently report "signed in" with no role, which sends
          // PostAuthRedirectService.resolve() down the wrong branch and can
          // bounce forever between a guarded route and /auth/login.
          this._authSession$.next(undefined);
          return null;
        }

        const merged = new User({
          ...user,
          firstName: existingUser.firstName,
          lastName: existingUser.lastName,
          role: existingUser.role,
          id: existingUser.id,
          email: existingUser.email,
        });
        this._authSession$.next(merged);
        this._fullUser$.next(existingUser);
        return this.assembleFromIUser(existingUser);
      } catch (err) {
        // Only a definitive rejection from the backend (invalid/unrecognized
        // session) should sign the user out here — a network blip or backend
        // 5xx during a routine silent-renewal /user/me call (see renew()) must
        // not clear a still-valid Cognito session, or the router guard can
        // bounce the user to /auth/login mid-navigation over a transient error.
        if (err instanceof HttpErrorResponse && (err.status === 401 || err.status === 403)) {
          this._authSession$.next(undefined);
        }
        return null;
      }
    } finally {
      this._sessionSettling$.next(this._sessionSettling$.value - 1);
    }
  }

  private restoringSession: Promise<void> | null = null;

  /** Idempotent, memoized session restore — safe to call from multiple
   *  concurrent callers (constructor + every currentUserOnceLoaded() caller);
   *  only the first actually does the work. */
  private restoreSessionOnce(): Promise<void> {
    if (!this.restoringSession) {
      this.restoringSession = this.restoreSession();
    }
    return this.restoringSession;
  }

  /** Reads CognitoUserPool's own persisted "last signed-in user" (backed by
   *  its default localStorage adapter — see cognito.service.ts) and, if a
   *  still-valid (or refreshable) session exists, applies it the same way a
   *  fresh sign-in does. `getSession()` transparently refreshes an expired
   *  session using its stored refresh token, same as amazon-cognito-identity-js
   *  does for any other caller. A no-op if nobody has ever signed in on this
   *  browser, or the refresh token itself has expired/been revoked. */
  private async restoreSession(): Promise<void> {
    // Belt-and-suspenders: the boot APP_INITIALIZER (app.config.ts) kicks
    // this off eagerly so it's normally already resolved by the time this
    // runs, but multi-provider APP_INITIALIZERs run in parallel, not
    // registration order — this direct await is what actually guarantees
    // getCurrentUser() below (the one synchronous storage read
    // amazon-cognito-identity-js makes) never sees an empty, not-yet-
    // hydrated cache and gets memoized here as "no session found" forever.
    await cognitoPreferencesStorage.hydrate();
    const cognitoUser = this.cognitoService.userPool.getCurrentUser();
    if (!cognitoUser) return;

    const session = await new Promise<CognitoUserSession | null>((resolve) => {
      cognitoUser.getSession((err: Error | null, result: CognitoUserSession | null) => {
        resolve(err ? null : result);
      });
    });
    if (!session || !session.isValid()) return;

    const idToken = session.getIdToken();
    const payload = idToken.decodePayload();
    const user: IUser = {
      authAccessToken: session.getAccessToken().getJwtToken(),
      authIdToken: idToken.getJwtToken(),
      authRenewToken: session.getRefreshToken().getToken(),
      authExpires: idToken.getExpiration() * 1000,
      email: (payload['email'] as string | undefined)?.toLocaleLowerCase(),
      firstName: payload['given_name'] as string | undefined,
      lastName: payload['family_name'] as string | undefined,
    };
    await this.applySession(user);
  }

  private renew(): Observable<'success' | 'deferred'> {
    return this.networkStatus.status$.pipe(
      first(),
      switchMap((status) => {
        if (status?.connected !== true) {
          return of('deferred' as const);
        }
        const user = this._authSession$.value;
        if (user?.authRenewToken && user?.authIdToken) {
          return this.cognitoService
            .renew(user.authRenewToken, user.authIdToken)
            .pipe(
              switchMap((userWithTokens) =>
                from(this.applySession({ id: user.id, role: user.role, ...userWithTokens })).pipe(
                  map(() => 'success' as const),
                ),
              ),
            );
        }
        return throwError(() => new Error('Unable to renew token'));
      }),
    );
  }

  /** Called by the JWT interceptor when a request is about to be made with an
   *  expired/near-expiry token. Throttled + coalesced so concurrent requests
   *  share one renewal (see the constructor). */
  triggerRenew(): void {
    this.renewTrigger$.next();
  }

  /** Observable the interceptor awaits after calling `triggerRenew()` — emits
   *  once the in-flight (or just-triggered) renewal settles. */
  get renewResult$(): Observable<'success' | 'deferred'> {
    return this._renewResult$.asObservable();
  }

  get authSession$(): Observable<IUser | undefined> {
    return this._authSession$.asObservable();
  }

  // ---- Internal: merge a real IUser with the local-only profile fields --

  private assembleFromIUser(user: IUser): IPublicUser {
    const email = user.email ?? '';
    const local = this.readLocalProfile()[email] || {};
    return {
      id: user.id,
      email,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      phone: user.phone || undefined,
      createdAt: createdToIso(user.created),
      // Not read back from the local profile: ID verification is switched off
      // (no provider checks anything), so a flag left in this browser by the
      // old mock flow must not show as "Verified" anywhere.
      photoUrl: user.photo?.key ? HelperFunctions.getPhotoUrlSquareSmall(user.photo) : undefined,
      notifPrefs: local.notifPrefs,
      affiliateRef: user.affiliateRef || undefined,
      role: user.role || undefined,
      source: user.source || undefined,
      instantBookingAllowed: !!user.instantBookingAllowed,
      affiliateType: user.affiliateType || undefined,
    };
  }

  private readLocalProfile(): ILocalProfile {
    if (!isPlatformBrowser(this.platformId)) return {};
    return this.localProfileStore.read(LOCAL_PROFILE_IDB_KEY) ?? {};
  }

  private writeLocalProfile(email: string, patch: ILocalProfile[string]): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const all = this.readLocalProfile();
    all[email] = { ...(all[email] || {}), ...patch };
    void this.localProfileStore.write(LOCAL_PROFILE_IDB_KEY, all);
  }
}

function friendlyError(e: unknown): string {
  if (e && typeof e === 'object') {
    // Calls that go to Cognito through HttpClient (password reset, resend) reject
    // with an HttpErrorResponse whose Cognito code sits in `error.__type`, e.g.
    // "CodeMismatchException" or "com.amazonaws...#CodeMismatchException". Reading
    // only `code`/`name` missed it, so a wrong reset code showed the raw
    // "Http failure response for https://cognito-idp…: 400 Bad Request" (a-recover).
    const body = (e as { error?: unknown }).error;
    const awsType = body && typeof body === 'object' ? (body as { __type?: unknown }).__type : undefined;
    const code =
      typeof awsType === 'string'
        ? awsType.split('#').pop()
        : (e as { code?: unknown }).code ?? (e as { name?: unknown }).name;
    const codeStr = typeof code === 'string' ? code : '';
    if (codeStr === 'UsernameExistsException') return 'An account with this email already exists.';
    if (codeStr === 'NotAuthorizedException') return 'Email or password is incorrect.';
    if (codeStr === 'UserNotFoundException') return 'Email or password is incorrect.';
    if (codeStr === 'CodeMismatchException') return 'That code is incorrect.';
    if (codeStr === 'ExpiredCodeException') return 'That code has expired. Request a new one.';
    if (codeStr === 'InvalidPasswordException') return "Password doesn't meet the policy (min 8 chars + complexity).";
    if (codeStr === 'LimitExceededException') return 'Too many attempts. Try again in a moment.';
    if (codeStr === 'PasswordResetRequiredException')
      return 'Your password needs to be reset. Use "Forgot password" to continue.';
    // An unmapped HTTP failure: Cognito's own message is readable; Angular's
    // "Http failure response for <url>" is not.
    if ((e as { name?: unknown }).name === 'HttpErrorResponse') {
      const awsMessage = body && typeof body === 'object' ? (body as { message?: unknown }).message : undefined;
      return typeof awsMessage === 'string' && awsMessage ? awsMessage : 'Something went wrong. Please try again.';
    }
    if ('message' in e) return String((e as { message: unknown }).message);
  }
  return 'Something went wrong. Please try again.';
}
